import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Users, UserCog, CalendarCheck, AlertTriangle, Clock, CheckCircle2,
  ShieldAlert, ArrowRight, X, HandHeart, Phone, Mail, Plane, CreditCard, ChevronDown,
} from "lucide-react";
import { statusTone, clientTag } from "@/lib/status";
import { AreaReminder } from "@/components/AreaReminder";

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-border bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-lg ${tint}`}>
        <Icon size={22} />
      </div>
      <p className="font-heading text-3xl font-bold text-ink">{value}</p>
      <p className="mt-1 text-sm text-ink-soft">{label}</p>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [missedFor, setMissedFor] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [myPay, setMyPay] = useState({ bank_account: "", ni_number: "" });
  const [payOpen, setPayOpen] = useState(false);
  const inpCls = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
  const isManager = ["admin", "office"].includes(user?.role);

  const savePay = async () => {
    try { await api.put("/me/payroll", myPay); toast.success("Payroll details saved"); }
    catch { toast.error("Could not save your details"); }
  };

  const load = () => api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/messages").then((r) => setMsgs(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/me/payroll").then((r) => setMyPay({ bank_account: r.data.bank_account || "", ni_number: r.data.ni_number || "" })).catch(() => {}); }, []);

  const dismissAlert = async (key) => {
    try {
      await api.post("/alerts/dismiss", { key });
      setStats((s) => s ? {
        ...s,
        visit_alerts: (s.visit_alerts || []).filter((a) => a.key !== key),
        service_requests: (s.service_requests || []).filter((r) => r.key !== key),
      } : s);
    } catch { /* noop */ }
  };

  if (!stats)
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
      </div>
    );

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <p className="text-sm font-medium uppercase tracking-wider text-ink-muted">
          {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
        </p>
        <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-ink sm:text-4xl">
          Hello, {user?.name?.split(" ")[0]}
        </h1>
        <p className="mt-1 text-ink-soft">Here's the pulse of your service today.</p>
        <div className="mt-3 h-1.5 w-24 rounded-full bg-gradient-to-r from-brand via-mint to-gold" />
      </div>

      {user?.role === "carer" && <AreaReminder />}

      {/* Alerts */}
      {(stats.alerts?.length > 0 || stats.rtw_alerts?.length > 0 || stats.compliance_alerts?.length > 0 || stats.document_alerts?.length > 0) && (
        <div className="space-y-3">
          {stats.alerts?.map((a, i) => (
            <div key={`a${i}`} className="flex items-center gap-3 rounded-r-lg border-l-4 border-coral bg-coral-light p-4">
              <AlertTriangle size={18} className="text-coral" />
              <span className="text-sm font-medium text-coral-dark">{a.message}</span>
            </div>
          ))}
          {isAdmin && stats.rtw_alerts?.map((a, i) => (
            <div key={`r${i}`} className="flex items-center gap-3 rounded-r-lg border-l-4 border-gold bg-gold-light p-4">
              <ShieldAlert size={18} className="text-gold-dark" />
              <span className="text-sm font-medium text-gold-dark">
                Right-to-work {a.status === "missing" ? "missing" : "expiring"} for {a.carer_name}
                {a.expiry_date ? ` (expires ${a.expiry_date})` : ""}
              </span>
            </div>
          ))}
          {isAdmin && stats.compliance_alerts?.map((a, i) => (
            <div key={`c${i}`} data-testid={`compliance-alert-${i}`} className="flex items-center gap-3 rounded-r-lg border-l-4 border-gold bg-gold-light p-4">
              <ShieldAlert size={18} className="text-gold-dark" />
              <span className="text-sm font-medium text-gold-dark">
                {a.item} {String(a.status).replace(/_/g, " ")} for {a.carer_name}
                {a.expiry_date ? ` (expires ${a.expiry_date})` : ""}
              </span>
            </div>
          ))}
          {isAdmin && stats.document_alerts?.map((a, i) => (
            <div key={`d${i}`} data-testid={`document-alert-${i}`} className={`flex items-center gap-3 rounded-r-lg border-l-4 p-4 ${a.status === "expired" ? "border-coral bg-coral-light" : "border-gold bg-gold-light"}`}>
              <ShieldAlert size={18} className={a.status === "expired" ? "text-coral" : "text-gold-dark"} />
              <span className={`text-sm font-medium ${a.status === "expired" ? "text-coral-dark" : "text-gold-dark"}`}>
                {a.doc_type} document {a.status} for {a.carer_name} (expires {a.expiry_date})
              </span>
            </div>
          ))}
        </div>
      )}

      {msgs.filter((m) => !m.read && (m.priority === "urgent" || m.priority === "important")).length > 0 && (
        <div className="space-y-3" data-testid="priority-messages">
          {msgs.filter((m) => !m.read && (m.priority === "urgent" || m.priority === "important")).map((m) => (
            <button key={m.id} onClick={() => navigate("/inbox")} data-testid={`priority-msg-${m.id}`}
              className={`flex w-full items-center gap-3 rounded-r-lg border-l-4 p-4 text-left transition-all hover:brightness-95 ${m.priority === "urgent" ? "border-coral bg-coral-light" : "border-gold bg-gold-light"}`}>
              <AlertTriangle size={18} className={`shrink-0 ${m.priority === "urgent" ? "text-coral-dark" : "text-gold-dark"}`} />
              <span className={`flex-1 text-sm font-medium ${m.priority === "urgent" ? "text-coral-dark" : "text-gold-dark"}`}>
                {m.priority === "urgent" ? "Urgent" : "Important"}: {m.subject} — from {m.sender_name}
              </span>
              <span className="shrink-0 rounded-full bg-white/60 px-2 py-0.5 text-[11px] font-semibold text-ink">Open inbox</span>
            </button>
          ))}
        </div>
      )}

      {stats.visit_alerts?.length > 0 && (
        <div className="space-y-3" data-testid="visit-alerts">
          {stats.visit_alerts.map((a, i) => (
            <div key={`v${i}`} className="flex items-center gap-2 rounded-r-lg border-l-4 border-gold bg-gold-light p-4">
              <button type="button" data-testid={`visit-alert-${i}`}
                onClick={() => isManager && setMissedFor(a)} disabled={!isManager}
                className={`flex flex-1 items-center gap-3 text-left ${isManager ? "cursor-pointer" : "cursor-default"}`}>
                <Clock size={18} className="shrink-0 text-gold-dark" />
                <span className="flex-1 text-sm font-medium text-gold-dark">
                  {a.type === "missed" ? "Missed visit" : "Not checked in yet"}: {a.client_name} with {a.carer_name} · {a.date} at {a.start_time}
                  {a.missed_reason ? ` — logged: ${a.missed_reason}` : ""}
                </span>
                {isManager && <span className="shrink-0 rounded-full bg-white/60 px-2 py-0.5 text-[11px] font-semibold text-gold-dark">{a.missed_reason ? "Edit note" : "Log reason"}</span>}
              </button>
              <button type="button" onClick={() => dismissAlert(a.key)} data-testid={`dismiss-visit-alert-${i}`}
                title="Dismiss" className="shrink-0 rounded-full p-1 text-gold-dark/70 hover:bg-white/60 hover:text-gold-dark">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {isManager && stats.service_requests?.length > 0 && (
        <div className="space-y-3" data-testid="service-request-alerts">
          {stats.service_requests.map((r) => (
            <div key={r.id} className="flex items-center gap-2 rounded-r-lg border-l-4 border-brand bg-brand-light/60 p-4">
              <button onClick={() => navigate("/clients")} data-testid={`service-request-alert-${r.id}`}
                className="flex flex-1 items-center gap-3 text-left">
                <HandHeart size={18} className="shrink-0 text-brand" />
                <span className="flex-1 text-sm font-medium text-brand-dark">
                  New service request: <span className="font-semibold">{r.service}</span> for {r.client_name} — from {r.requested_by}
                </span>
                <span className="shrink-0 rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-semibold text-brand-dark">Review</span>
              </button>
              <button type="button" onClick={() => dismissAlert(r.key)} data-testid={`dismiss-request-alert-${r.id}`}
                title="Dismiss" className="shrink-0 rounded-full p-1 text-brand/70 hover:bg-white/60 hover:text-brand">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div data-testid="office-contact" className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-brand/20 bg-brand-light/40 p-4">
        <span className="text-xs font-semibold uppercase tracking-wider text-brand">Office contact</span>
        <a href="tel:+447761329702" className="inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-brand"><Phone size={15} /> 07761 329702</a>
        <a href="mailto:hello@bsbservices.co.uk" className="inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-brand"><Mail size={15} /> hello@bsbservices.co.uk</a>
      </div>

      {isManager && stats.absences_today?.length > 0 && (
        <div data-testid="absence-card" className="rounded-xl border border-coral/20 bg-coral-light/40 p-5">
          <p className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-ink"><Plane size={18} className="text-coral" /> Off today</p>
          <div className="flex flex-wrap gap-2">
            {stats.absences_today.map((a) => (
              <span key={a.id} data-testid={`absence-${a.id}`} className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm shadow-sm">
                <span className="font-medium text-ink">{a.subject_name}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${a.kind === "sickness" ? "bg-coral text-white" : "bg-gold text-white"}`}>{a.kind === "sickness" ? "Sick" : a.subject_type === "client" ? "Holiday" : "Leave"}</span>
                <span className="text-xs text-ink-muted">until {a.end_date}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div data-testid="my-payroll-card" className="rounded-xl border border-border bg-white p-5 shadow-sm">
        <button onClick={() => setPayOpen((v) => !v)} data-testid="toggle-my-payroll" className="flex w-full items-center justify-between">
          <span className="flex items-center gap-2 font-heading text-lg font-semibold text-ink"><CreditCard size={18} className="text-brand" /> My payroll details</span>
          <ChevronDown size={18} className={`text-ink-muted transition-transform ${payOpen ? "rotate-180" : ""}`} />
        </button>
        {payOpen && (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><label className="mb-1 block text-xs font-medium text-ink-muted">Bank details (name · sort code · account no.)</label><input data-testid="my-bank-input" className={inpCls} value={myPay.bank_account} onChange={(e) => setMyPay({ ...myPay, bank_account: e.target.value })} placeholder="e.g. Halifax · 11-22-33 · 12345678" /></div>
            <div><label className="mb-1 block text-xs font-medium text-ink-muted">National Insurance number</label><input data-testid="my-ni-input" className={inpCls} value={myPay.ni_number} onChange={(e) => setMyPay({ ...myPay, ni_number: e.target.value })} placeholder="e.g. QQ123456C" /></div>
            <div className="sm:col-span-2"><button onClick={savePay} data-testid="save-my-payroll" className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark">Save my details</button><p className="mt-2 text-xs text-ink-muted">Only you and the office/admin can see this. Used for payroll.</p></div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {isAdmin && <Stat icon={Users} label="Active clients" value={stats.total_clients} tint="bg-brand-light text-brand" />}
        {isAdmin && <Stat icon={UserCog} label="Befrienders" value={stats.total_staff} tint="bg-slateblue-light text-slateblue" />}
        <Stat icon={CalendarCheck} label="Visits today" value={stats.visits_today} tint="bg-sage-light text-sage-dark" />
        <Stat icon={CheckCircle2} label="Completed today" value={stats.completed_today} tint="bg-sage-light text-sage-dark" />
      </div>

      {/* Today's schedule */}
      <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-xl font-semibold text-ink">Today's schedule</h2>
          <button
            onClick={() => navigate("/roster")}
            data-testid="view-roster-link"
            className="flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark"
          >
            View rota <ArrowRight size={16} />
          </button>
        </div>
        {stats.todays_visits.length === 0 ? (
          <p className="py-8 text-center text-ink-muted">No visits scheduled for today.</p>
        ) : (
          <div className="space-y-3">
            {stats.todays_visits.map((v) => (
              <button
                key={v.id}
                onClick={() => navigate(`/visits/${v.id}`)}
                data-testid={`dashboard-visit-${v.id}`}
                className="flex w-full items-center gap-4 rounded-lg border border-border bg-ivory/60 p-4 text-left transition-colors hover:border-brand/40 hover:bg-brand-light/50"
              >
                <div className="flex flex-col items-center rounded-lg bg-white px-3 py-2 shadow-sm">
                  <Clock size={14} className="text-brand" />
                  <span className="mt-1 text-xs font-semibold text-ink">{v.start_time}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 truncate font-semibold text-ink">
                    {clientTag(v.client_colour) && <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${clientTag(v.client_colour).dot}`} />}
                    {v.client_name}
                  </p>
                  <p className="truncate text-sm text-ink-soft">with {v.carer_name}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${statusTone(v.status)}`}>
                  {v.status.replace("_", " ")}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {missedFor && (
        <MissedReasonModal alert={missedFor} onClose={() => setMissedFor(null)} onSaved={() => { setMissedFor(null); load(); }} />
      )}
    </div>
  );
}

function MissedReasonModal({ alert, onClose, onSaved }) {
  const [reason, setReason] = useState(alert.missed_reason || "");
  const [contacted, setContacted] = useState(alert.missed_contacted || "");
  const [saving, setSaving] = useState(false);
  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
  const save = async () => {
    setSaving(true);
    try { await api.post(`/visits/${alert.id}/missed-reason`, { reason, contacted }); toast.success("Reason logged"); onSaved(); }
    catch (e) { toast.error("Could not save"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div data-testid="missed-reason-modal" className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-ink">Log missed / late visit</h2>
          <button onClick={onClose}><X size={20} className="text-ink-muted" /></button>
        </div>
        <p className="mb-4 text-sm text-ink-soft">{alert.client_name} with {alert.carer_name} · {alert.date} at {alert.start_time}</p>
        <label className="mb-1 block text-sm font-medium text-ink">What happened / why was it missed?</label>
        <textarea rows={3} data-testid="missed-reason-input" className={input} value={reason} onChange={(e) => setReason(e.target.value)} />
        <label className="mb-1 mt-4 block text-sm font-medium text-ink">Who was contacted?</label>
        <input data-testid="missed-contacted-input" className={input} placeholder="e.g. Client's daughter, office manager" value={contacted} onChange={(e) => setContacted(e.target.value)} />
        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="save-missed-reason-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
